/**
 * TaxSetu AI - Auth Modal Component
 * ==================================
 * Professional sign-in modal with Google OAuth.
 * Works on ALL browsers - Chrome, Firefox, Edge, Safari.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { X, Loader2, Shield, Zap, History, FileSearch, Bell, Crown, CheckCircle } from 'lucide-react';
import { API_BASE_URL } from '../utils/api';

// Actual TaxSetu Logo - Infinity symbol with T & S
const TAXSETU_LOGO = "https://customer-assets.emergentagent.com/job_taxgpt-india-1/artifacts/eq72g3c3_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

// TaxSetu Logo Component
const TaxSetuLogo = ({ size = 64, showPro = false }) => (
  <div className="relative">
    <img 
      src={TAXSETU_LOGO} 
      alt="TaxSetu AI" 
      style={{ width: size, height: size }}
      className="object-contain"
    />
    {showPro && (
      <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/30">
        <Crown className="w-3.5 h-3.5 text-white" />
      </div>
    )}
  </div>
);

export default function AuthModal({ isOpen, onClose, trigger = 'limit', queriesUsed = 0, maxQueries = 1 }) {
  const { loginWithGoogle, handleOneTapCallback, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleClientId, setGoogleClientId] = useState(null);
  const [authMethod, setAuthMethod] = useState('google'); // 'google' or 'email'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  // Fetch Google Client ID and initialize One-Tap
  useEffect(() => {
    if (!isOpen || user) return;
    
    const initGoogleOneTap = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/google/client-id`);
        const data = await response.json();
        
        if (data.client_id) {
          setGoogleClientId(data.client_id);
          
          // Wait for Google Identity Services to load
          const waitForGoogle = setInterval(() => {
            if (window.google?.accounts?.id) {
              clearInterval(waitForGoogle);
              
              // Initialize Google One-Tap with FedCM support
              window.google.accounts.id.initialize({
                client_id: data.client_id,
                callback: handleOneTapResponse,
                auto_select: true,
                cancel_on_tap_outside: false,
                context: 'signin',
                ux_mode: 'popup',
                itp_support: true,
                use_fedcm_for_prompt: true // FedCM migration - required by Google
              });
              
              // Show One-Tap prompt (optional - may not work on all browsers)
              window.google.accounts.id.prompt();
            }
          }, 100);
          
          setTimeout(() => clearInterval(waitForGoogle), 5000);
        }
      } catch (err) {
        console.error('Failed to initialize Google One-Tap:', err);
      }
    };
    
    initGoogleOneTap();
  }, [isOpen, user]);

  const handleOneTapResponse = async (response) => {
    setLoading(true);
    setError('');
    
    try {
      await handleOneTapCallback(response);
      onClose();
    } catch (err) {
      setError('Sign-in failed. Please try again.');
      setLoading(false);
    }
  };

  // Email OTP functions
  const handleSendOTP = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    setOtpLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/email/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send OTP');
      }
      
      setOtpSent(true);
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    
    setOtpLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/email/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid OTP');
      }
      
      // Store token and user data
      localStorage.setItem('taxsetu_token', data.access_token);
      localStorage.setItem('taxsetu_user', JSON.stringify(data.user));
      
      // Reload to reflect login state
      window.location.reload();
      
    } catch (err) {
      setError(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      await loginWithGoogle();
    } catch (err) {
      setError('Failed to initiate sign-in. Please try again.');
      setLoading(false);
    }
  };

  const isProUpgrade = trigger === 'pro' || trigger === 'upgrade_to_pro';
  const isQueryLimit = trigger === 'query_limit' || trigger === 'limit';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[#0a0f1a] rounded-2xl max-w-md w-full mx-4 shadow-2xl border border-[#1e3a5f]/30 animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
        
        {/* Premium Header Gradient */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#38bdf8] via-[#0ea5e9] to-[#0284c7]" />
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <TaxSetuLogo size={64} showPro={isProUpgrade} />
          </div>
          
          {/* Brand Name */}
          <div className="text-center mb-2">
            <h1 className="text-2xl font-bold text-white tracking-tight">
              Tax<span className="text-[#38bdf8]">Setu</span> AI
              {isProUpgrade && <span className="text-amber-400 ml-2">Pro</span>}
            </h1>
          </div>

          {isProUpgrade ? (
            /* Pro Upgrade Content */
            <>
              <p className="text-center text-gray-400 text-sm mb-6">
                Elevate your tax intelligence
              </p>
              
              <div className="bg-[#0f2744]/50 rounded-xl p-4 mb-6 border border-[#1e3a5f]/30">
                <h3 className="text-white font-semibold text-center mb-4">
                  Unlock Premium Features
                </h3>
                
                <div className="space-y-3">
                  <FeatureItem 
                    icon={Zap} 
                    title="Unlimited AI Queries" 
                    description="No daily limits on expert tax consultations"
                    highlight
                  />
                  <FeatureItem 
                    icon={FileSearch} 
                    title="Notice Analyzer" 
                    description="AI-powered analysis of IT department notices"
                  />
                  <FeatureItem 
                    icon={Bell} 
                    title="Donation Notice Reply" 
                    description="Smart response drafting for political donation notices"
                  />
                  <FeatureItem 
                    icon={History} 
                    title="Search History" 
                    description="Access all your past queries anytime"
                  />
                </div>
              </div>

              <button
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-semibold py-3.5 px-4 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-amber-500/20"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Crown className="w-5 h-5" />
                    <span>Upgrade to Pro</span>
                  </>
                )}
              </button>
              
              <p className="text-center text-gray-500 text-xs mt-4">
                Sign in first to unlock Pro features
              </p>
            </>
          ) : (
            /* Regular Sign In Content - Google + Email OTP */
            <>
              <p className="text-center text-gray-400 text-sm mb-4">
                Your AI-Powered Tax Expert
              </p>
              
              {/* Query Limit Warning */}
              {isQueryLimit && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 mb-4 text-center">
                  <p className="text-cyan-400 font-medium mb-1">
                    Sign in to continue
                  </p>
                  <p className="text-gray-400 text-xs sm:text-sm">
                    Get unlimited access to TaxSetu AI
                  </p>
                </div>
              )}
              
              {/* Benefits */}
              <div className="space-y-3 mb-5">
                <BenefitItem icon={Zap} text="Unlimited tax queries" />
                <BenefitItem icon={History} text="Save your search history" />
                <BenefitItem icon={Shield} text="Safe and Private" />
              </div>
              
              {/* Auth Method Tabs */}
              <div className="flex mb-4 bg-gray-800/50 rounded-lg p-1">
                <button
                  onClick={() => { setAuthMethod('google'); setError(''); }}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    authMethod === 'google' 
                      ? 'bg-cyan-600 text-white' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Google
                </button>
                <button
                  onClick={() => { setAuthMethod('email'); setError(''); }}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    authMethod === 'email' 
                      ? 'bg-cyan-600 text-white' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Email OTP
                </button>
              </div>
              
              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm text-center">
                  {error}
                </div>
              )}
              
              {/* Fixed height container */}
              <div className="min-h-[130px]">
                {authMethod === 'google' ? (
                /* Google Sign-In */
                <div className="mb-2">
                  <button
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-800 font-medium py-3.5 px-4 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-gray-600" />
                    ) : (
                      <>
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        <span>Continue with Google</span>
                      </>
                    )}
                  </button>
                  <p className="text-center text-gray-500 text-xs mt-3">
                    Works on all browsers
                  </p>
                </div>
              ) : (
                /* Email OTP Sign-In */
                <div className="space-y-3">
                  {!otpSent ? (
                    <>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                      />
                      <button
                        onClick={handleSendOTP}
                        disabled={otpLoading || !email}
                        className="w-full flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-3.5 px-4 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {otpLoading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Bell className="w-5 h-5" />
                            <span>Send OTP</span>
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-center text-green-400 text-sm mb-2">
                        ✓ OTP sent to {email}
                      </p>
                      <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 text-center text-xl tracking-widest"
                      />
                      <button
                        onClick={handleVerifyOTP}
                        disabled={otpLoading || otp.length !== 6}
                        className="w-full flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-3.5 px-4 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {otpLoading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-5 h-5" />
                            <span>Verify & Sign In</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => { setOtpSent(false); setOtp(''); }}
                        className="w-full text-gray-400 text-sm hover:text-white"
                      >
                        Change email / Resend OTP
                      </button>
                    </>
                  )}
                  <p className="text-center text-gray-500 text-xs">
                    Works with Gmail & all email providers
                  </p>
                </div>
              )}
              </div>
            </>
          )}
          
          {/* Terms */}
          <div className="text-center mt-6 pt-4 border-t border-gray-800">
            <p className="text-white/80 font-medium text-sm mb-1">
              ✨ One-tap login, let's start your tax journey!
            </p>
            <p className="text-xs text-gray-600">
              By continuing, you agree to our{' '}
              <a href="/terms" className="text-[#38bdf8] hover:underline">Terms</a>
              {' & '}
              <a href="/privacy" className="text-[#38bdf8] hover:underline">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function BenefitItem({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="w-9 h-9 rounded-lg bg-[#38bdf8]/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-[#38bdf8]" />
      </div>
      <span className="text-gray-300">{text}</span>
    </div>
  );
}

function FeatureItem({ icon: Icon, title, description, highlight = false }) {
  return (
    <div className="flex items-start gap-3">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
        highlight ? 'bg-gradient-to-r from-amber-500/20 to-amber-600/20' : 'bg-[#38bdf8]/10'
      }`}>
        <Icon className={`w-4 h-4 ${highlight ? 'text-amber-400' : 'text-[#38bdf8]'}`} />
      </div>
      <div>
        <p className={`text-sm font-medium ${highlight ? 'text-amber-400' : 'text-white'}`}>{title}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
    </div>
  );
}
